import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, Edit2 } from "lucide-react";
import axios from "axios";
import Swal from "sweetalert2";
import AddressForm from "../AddressForm";
import RegisterModal from "../modals/RegisterModal";
import PKSOFT_URLS from "../../utilities/Pk_api_Urls";
import { getEncodedTimestamp } from "../../utilities/dateUtils";
import { handleAuthDialog } from "../../utilities/authDialogIntegration";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store/store";
import {
  resolveUid,
  getOrCreateRandomUserId,
  getCookieValue,
  deleteCookie,
} from "../../utilities/cookieUtils";

interface Props {
  savedAddress: any;
  onChange: () => void;
  onAddressChange?: (addressId: string) => void;
}

const AddressSummary: React.FC<Props> = ({ savedAddress, onChange, onAddressChange }) => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showMoreAddresses, setShowMoreAddresses] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [isGuestRegistration, setIsGuestRegistration] = useState(false);
  const [registrationMode, setRegistrationMode] = useState<"guest" | "user" | null>(null);

  // Ref to track success synchronously to avoid closure staleness in onHide
  const authSuccessRef = React.useRef(false);

  const dispatch = useDispatch<AppDispatch>();

  const triggerAuthDialog = () => {
    // Delegate to centralized handler which knows how to perform guest auth
    handleAuthDialog(dispatch, navigate);
  };

  // Fetch user addresses
  useEffect(() => {
    fetchAddresses();
  }, []);


  const fetchAddresses = async () => {
    const userid = resolveUid();
    if (!userid) {
      setLoading(false);
      return;
    }

    const url = `${PKSOFT_URLS.ADDRESS.GET}/${userid}`;
    const payload = { date: getEncodedTimestamp() };
    setLoading(true);

    try {
      const res = await axios({
        method: "post",
        url,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(payload),
      });
      const { messageCode, data } = res.data;

      // ✅ Check if user not verified (messageCode 102)
      if (messageCode === 102) {
        setLoading(false);
        triggerAuthDialog();
        return;
      }

      // ✅ Handle successful response
      const addressData = data || [];

      const mapped = addressData.map((item: any) => ({
        id: item.id,
        fullName: item.fullName,
        mobileNo: item.mobileNo,
        email: item.email ?? "",
        addressLine1: item.addressLine1,
        addressLine2: item.addressLine2,
        landMark: item.landMark,
        cityName: item.cityName,
        stateName: item.stateName,
        countryName: item.countryName,
        pinCode: item.pinCode,
        isPrimary: item.isPrimary,
      }));

      setAddresses(mapped);

      // Prefer the primary address, but fall back to the first one — when no
      // address is flagged primary, "No address found" would otherwise show
      // and checkout would be stuck with no way to pick the ones that exist.
      const preferred = mapped.find((a: any) => a.isPrimary) ?? mapped[0] ?? null;
      if (preferred) {
        setSelectedAddress(preferred);
        onAddressChange?.(preferred.id);
      } else {
        setSelectedAddress(null);
      }
    } catch (err) {
      console.error("Failed to fetch addresses", err);
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (addr: any) => {
    const userid = resolveUid();

    const payload = {
      ...addr,
      code: addr.id ? addr.id : null,
      userCode: userid,
      d: getEncodedTimestamp(),
    };

    try {
      let response;

      if (addr.id) {
        // Update
        response = await axios.post(
          `${PKSOFT_URLS.ADDRESS.UPDATE}/${addr.id}`,
          payload
        );
      } else {
        // Create
        response = await axios.post(PKSOFT_URLS.ADDRESS.CREATE, payload);
      }

      // ✅ Check if user not verified (messageCode 102)
      if (response.data?.messageCode === 102) {
        // Cache the address data for later use after login
        // localStorage.setItem("pendingAddress", JSON.stringify(addr));
        setShowAddressModal(false);

        triggerAuthDialog();
        return;
      }
      // ✅ Success - address saved
      Swal.fire(
        "Success",
        addr.id ? "Address updated successfully" : "Address saved successfully",
        "success"
      );
      // Refresh addresses list
      await fetchAddresses();
      setShowAddressModal(false);
      setEditingAddress(null);

      // Clear pending address if successful
      // localStorage.removeItem("pendingAddress");

    } catch (err) {
      console.error("Address save error:", err);
      Swal.fire("Error", "Failed to save address", "error");
    }
  };

  const handleSelectAddress = (address: any) => {
    setSelectedAddress(address);
    onAddressChange?.(address.id);
    setShowMoreAddresses(false);
  };

  if (loading) {
    return (
      <div className="card border rounded p-3 mb-3">
        <h6 className="fw-semibold mb-2">Delivery Address</h6>
        <p className="text-muted small">Loading addresses...</p>
      </div>
    );
  }

  return (
    <>
      <div className="card border rounded p-3 mb-3">
        <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
          <MapPin size={16} />
          Delivery Address
        </h6>

        {selectedAddress ? (
          <div className="p-3 border rounded mb-2" style={{ backgroundColor: "#f9f9f9" }}>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <strong style={{ fontSize: "15px" }}>{selectedAddress.fullName}</strong>
                {selectedAddress.isPrimary && (
                  <span className="badge bg-success ms-2" style={{ fontSize: "11px" }}>
                    DEFAULT
                  </span>
                )}
              </div>
            </div>

            <p className="m-0 small text-muted mb-2" style={{ lineHeight: "1.6" }}>
              {selectedAddress.addressLine1}
              {selectedAddress.addressLine2 && `, ${selectedAddress.addressLine2}`}
              {selectedAddress.landMark && `, ${selectedAddress.landMark}`}
              <br />
              {selectedAddress.cityName}, {selectedAddress.stateName} -{" "}
              {selectedAddress.pinCode}
              <br />
              {selectedAddress.countryName}
            </p>

            <p className="m-0 small text-muted d-flex align-items-center gap-1">
              <strong>Mobile:</strong> {selectedAddress.mobileNo}
            </p>

            {addresses.length > 1 && (
              <button
                className="btn btn-outline-secondary btn-sm mt-3 w-100"
                onClick={() => setShowMoreAddresses(!showMoreAddresses)}
              >
                {showMoreAddresses ? "Hide Other Addresses" : "Show Other Addresses"}
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <MapPin size={32} className="text-muted mb-2" />
            <p className="text-muted mb-3">No address found</p>
          </div>
        )}

        {/* Other Addresses */}
        {showMoreAddresses && addresses.length > 1 && (
          <div className="mt-3">
            <div className="small fw-semibold mb-2" style={{ color: "#282c3f" }}>
              Other Addresses:
            </div>
            {addresses
              .filter((a) => a.id !== selectedAddress?.id)
              .map((address) => (
                <div
                  key={address.id}
                  className="p-2 border rounded mb-2 cursor-pointer"
                  style={{
                    backgroundColor: "#f9f9f9",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                  onClick={() => handleSelectAddress(address)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <small style={{ fontSize: "13px" }}>
                        <strong>{address.fullName}</strong>
                      </small>
                      <p className="m-0 small text-muted" style={{ fontSize: "12px" }}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                        <br />
                        {address.cityName}, {address.stateName}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* Add/Change Address Button */}
        <button
          className="btn btn-outline-primary btn-sm w-100 mt-3 d-flex align-items-center justify-content-center gap-2"
          onClick={() => {
            setEditingAddress(null);
            setShowAddressModal(true);
          }}
        >
          <Plus size={16} />
          Add New Address
        </button>
      </div>

      {/* Address Form Modal */}
      <AddressForm
        show={showAddressModal}
        onHide={() => {
          setShowAddressModal(false);
          setEditingAddress(null);
        }}
        initial={editingAddress}
        onSave={handleSaveAddress}
      />

      {/* Guest Registration Modal */}
      <RegisterModal
        show={showRegisterModal}
        onHide={() => {
          setShowRegisterModal(false);
          setIsGuestRegistration(false);
          setRegistrationMode(null);
          // Show auth dialog again if failed/closed
          if (!authSuccessRef.current) {
            triggerAuthDialog();
          }
        }}
        isGuest={isGuestRegistration}
        title={
          registrationMode === "guest"
            ? "Registration as Guest"
            : registrationMode === "user"
              ? "Create a New Account"
              : undefined
        }
        onSuccess={() => {
          authSuccessRef.current = true;
          setShowRegisterModal(false);
          setIsGuestRegistration(false);
          setRegistrationMode(null);
        }}
      />
    </>
  );
};

export default AddressSummary;
